/**
 * TokenManager handles JWT token fetching and management for the standalone search app
 * Uses the same authentication flow as the main app
 *
 * Manages both wsToken (short-lived for WebSocket) and backendToken (longer-lived for API calls)
 */

interface TokenResponse {
	wsToken: string
	wsExpiresIn: number
	backendToken: string
	backendExpiresIn: number
}

interface TokenSeed extends TokenResponse {}

export class TokenManager {
	private static instance: TokenManager

	// WebSocket token (short-lived)
	private wsToken: string | null = null
	private wsTokenExpiry: number = 0

	// Backend token (longer-lived for API calls)
	private backendToken: string | null = null
	private backendTokenExpiry: number = 0

	private refreshTimer: NodeJS.Timeout | null = null
	private storeUrl: string | null = null
	private userId: string | null = null
	private contextGeneration = 0

	private constructor() {
		// Load tokens from sessionStorage if available
		this.loadFromStorage()
	}

	static getInstance(): TokenManager {
		if (!TokenManager.instance) {
			TokenManager.instance = new TokenManager()
		}
		return TokenManager.instance
	}

	/**
	 * Get the backend URL from environment or default to localhost
	 */
	private getBackendUrl(): string {
		return import.meta.env.VITE_BACKEND_URL || "http://localhost:8080"
	}

	/**
	 * Fetch a new token from the backend
	 * Returns both wsToken and backendToken
	 */
	async fetchToken(userId?: string): Promise<TokenResponse> {
		const backendUrl = this.getBackendUrl()
		if (userId !== undefined) this.userId = userId || null
		const generation = this.contextGeneration
		const url = new URL(`${backendUrl}/auth/token`)
		if (this.userId) url.searchParams.set("userId", this.userId)
		if (this.storeUrl) url.searchParams.set("storeUrl", this.storeUrl)

		console.log("[TokenManager] Backend URL:", backendUrl)
		console.log("[TokenManager] Fetching token from:", url)

		try {
			const response = await fetch(url.toString())
			if (!response.ok) {
				throw new Error(`Failed to fetch token: ${response.status} ${response.statusText}`)
			}

			const data: TokenResponse = await response.json()
			if (generation !== this.contextGeneration) throw new Error("Session changed during token renewal")

			// Store wsToken
			this.wsToken = data.wsToken
			this.wsTokenExpiry = Date.now() + data.wsExpiresIn * 1000

			// Store backendToken
			this.backendToken = data.backendToken
			this.backendTokenExpiry = Date.now() + data.backendExpiresIn * 1000

			// Save to sessionStorage
			this.saveToStorage()

			// Schedule refresh for backend token (5 seconds before expiry)
			this.scheduleRefresh(data.backendExpiresIn - 5)

			console.log("[TokenManager] Tokens fetched successfully:")
			console.log("  - wsToken expires in:", data.wsExpiresIn, "seconds")
			console.log("  - backendToken expires in:", data.backendExpiresIn, "seconds")

			return data
		} catch (error) {
			console.error("[TokenManager] Error fetching token:", error)
			throw error
		}
	}

	/**
	 * Get the current wsToken (for WebSocket), fetching a new one if needed
	 */
	async getToken(userId?: string): Promise<string> {
		// Check if wsToken exists and is not expired
		if (this.wsToken && Date.now() < this.wsTokenExpiry - 5000) {
			return this.wsToken
		}

		// Fetch new token (both ws and backend)
		const data = await this.fetchToken(userId)
		return data.wsToken
	}

	/**
	 * Get the current backendToken (for API calls), fetching a new one if needed
	 */
	async getBackendToken(userId?: string): Promise<string> {
		// Check if backendToken exists and is not expired
		if (this.backendToken && Date.now() < this.backendTokenExpiry - 5000) {
			return this.backendToken
		}

		// Fetch new token (both ws and backend)
		const data = await this.fetchToken(userId)
		return data.backendToken
	}

	/**
	 * Seed tokens from an externally created session response
	 * (e.g. reverse-demo store URL -> session endpoint)
	 */
	setTokensFromSession(tokens: TokenSeed, storeUrl: string, userId?: string): void {
		if (!storeUrl.trim()) throw new Error("Store URL is required for session tokens")
		this.contextGeneration++
		this.storeUrl = storeUrl.trim()
		this.userId = userId || null
		this.wsToken = tokens.wsToken
		this.wsTokenExpiry = Date.now() + tokens.wsExpiresIn * 1000

		this.backendToken = tokens.backendToken
		this.backendTokenExpiry = Date.now() + tokens.backendExpiresIn * 1000

		this.saveToStorage()
		this.scheduleRefresh(tokens.backendExpiresIn - 5)

		console.log("[TokenManager] Tokens seeded from external session")
	}

	/**
	 * Check if wsToken is valid (exists and not expired)
	 */
	isWsTokenValid(): boolean {
		return this.wsToken !== null && Date.now() < this.wsTokenExpiry - 5000
	}

	/**
	 * Check if backendToken is valid (exists and not expired)
	 */
	isBackendTokenValid(): boolean {
		return this.backendToken !== null && Date.now() < this.backendTokenExpiry - 5000
	}

	/**
	 * Clear all tokens
	 */
	clearToken(): void {
		this.contextGeneration++
		this.storeUrl = null
		this.userId = null
		sessionStorage.removeItem("in5_search_auth_store_url")
		sessionStorage.removeItem("in5_search_auth_user_id")
		this.wsToken = null
		this.wsTokenExpiry = 0
		this.backendToken = null
		this.backendTokenExpiry = 0

		sessionStorage.removeItem("in5_search_ws_token")
		sessionStorage.removeItem("in5_search_ws_token_expiry")
		sessionStorage.removeItem("in5_search_backend_token")
		sessionStorage.removeItem("in5_search_backend_token_expiry")

		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer)
			this.refreshTimer = null
		}
	}

	/**
	 * Stop automatic token refresh
	 * Call this after WebSocket connection is established (we don't need wsToken refresh anymore)
	 */
	stopRefresh(): void {
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer)
			this.refreshTimer = null
			console.log("[TokenManager] Stopped automatic token refresh")
		}
	}

	/**
	 * Save tokens to sessionStorage
	 */
	private saveToStorage(): void {
		if (this.storeUrl) sessionStorage.setItem("in5_search_auth_store_url", this.storeUrl)
		else sessionStorage.removeItem("in5_search_auth_store_url")
		if (this.userId) sessionStorage.setItem("in5_search_auth_user_id", this.userId)
		else sessionStorage.removeItem("in5_search_auth_user_id")
		if (this.wsToken) {
			sessionStorage.setItem("in5_search_ws_token", this.wsToken)
			sessionStorage.setItem("in5_search_ws_token_expiry", this.wsTokenExpiry.toString())
		}
		if (this.backendToken) {
			sessionStorage.setItem("in5_search_backend_token", this.backendToken)
			sessionStorage.setItem("in5_search_backend_token_expiry", this.backendTokenExpiry.toString())
		}
	}

	/**
	 * Load tokens from sessionStorage
	 */
	private loadFromStorage(): void {
		// Migrate sessions seeded before renewal context was stored separately.
		this.storeUrl =
			sessionStorage.getItem("in5_search_auth_store_url") || sessionStorage.getItem("in5_search_active_store_url")
		this.userId = sessionStorage.getItem("in5_search_auth_user_id")
		// Load wsToken
		const wsToken = sessionStorage.getItem("in5_search_ws_token")
		const wsExpiry = sessionStorage.getItem("in5_search_ws_token_expiry")

		if (wsToken && wsExpiry) {
			this.wsToken = wsToken
			this.wsTokenExpiry = parseInt(wsExpiry, 10)

			// Check if wsToken is still valid
			if (Date.now() >= this.wsTokenExpiry) {
				this.wsToken = null
				this.wsTokenExpiry = 0
				sessionStorage.removeItem("in5_search_ws_token")
				sessionStorage.removeItem("in5_search_ws_token_expiry")
			}
		}

		// Load backendToken
		const backendToken = sessionStorage.getItem("in5_search_backend_token")
		const backendExpiry = sessionStorage.getItem("in5_search_backend_token_expiry")

		if (backendToken && backendExpiry) {
			this.backendToken = backendToken
			this.backendTokenExpiry = parseInt(backendExpiry, 10)

			// Check if backendToken is still valid
			if (Date.now() >= this.backendTokenExpiry) {
				this.backendToken = null
				this.backendTokenExpiry = 0
				sessionStorage.removeItem("in5_search_backend_token")
				sessionStorage.removeItem("in5_search_backend_token_expiry")
			} else {
				// Schedule refresh for backend token
				const secondsUntilExpiry = Math.floor((this.backendTokenExpiry - Date.now()) / 1000)
				this.scheduleRefresh(Math.max(0, secondsUntilExpiry - 5))
			}
		}
	}

	/**
	 * Schedule automatic token refresh (for backendToken)
	 */
	private scheduleRefresh(seconds: number): void {
		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer)
		}

		if (seconds > 0) {
			console.log("[TokenManager] Scheduling token refresh in", seconds, "seconds")
			this.refreshTimer = setTimeout(() => {
				console.log("[TokenManager] Auto-refreshing token")
				this.fetchToken().catch((error) => {
					console.error("[TokenManager] Auto-refresh failed:", error)
				})
			}, seconds * 1000)
		}
	}
}
